import { type ReactNode, useId } from 'react';
import { cn } from '../cn.js';

export interface FormFieldRenderProps {
  readonly id: string;
  readonly 'aria-describedby': string | undefined;
  readonly 'aria-invalid': true | undefined;
  readonly required: boolean;
}

export interface FormFieldProps {
  readonly label: string;
  /** What the field is for. Shown under the label, and read out with it. */
  readonly description?: string;
  /** A refusal. Replaces nothing — it is announced *and* the description stays. */
  readonly error?: string;
  /** A hint about format. Shown under the control, where somebody looks after typing. */
  readonly hint?: string;
  readonly required?: boolean;
  readonly className?: string;
  /** The control. It receives the id and the ARIA wiring; it must spread them. */
  readonly children: (props: FormFieldRenderProps) => ReactNode;
}

/**
 * A labelled field, wired correctly.
 *
 * **Why a render prop and not a wrapper.** The association between a label, a description, an
 * error and a control is four `id`s and three attributes, and every screen that does it by hand
 * does it slightly differently — twenty files in this repository hand-wrote a `<label>` beside an
 * input, and the ones using a Base UI control were wrong in a way that looks right: Base UI
 * renders a checkbox as a `button`, so a wrapping `<label>` never associated at all. Handing the
 * control its props makes the wiring the component's job and not the page's.
 *
 * The label is **always visible**. A placeholder is not a label: it disappears exactly when
 * somebody is filling the field in and needs it, and it is invisible to a screen reader as a name.
 *
 * An error does not replace the description. Somebody who has just been refused needs to know
 * both what went wrong and what the field was for.
 */
export function FormField({
  label,
  description,
  error,
  hint,
  required = false,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  // Order matters: a screen reader reads these in the order they are listed, and the error is the
  // thing the person needs first.
  const describedBy =
    [
      error === undefined ? undefined : errorId,
      description === undefined ? undefined : descriptionId,
      hint === undefined ? undefined : hintId,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-(--space-xs)', className)}>
      <label htmlFor={id} className="text-(length:--font-size-small) text-(--color-text-secondary)">
        {label}
        {required ? (
          <>
            {' '}
            <span aria-hidden="true" className="text-(--color-danger)">
              *
            </span>
            <span className="sr-only">(required)</span>
          </>
        ) : null}
      </label>

      {description === undefined ? null : (
        <span
          id={descriptionId}
          className="text-(length:--font-size-caption) text-(--color-text-muted)"
        >
          {description}
        </span>
      )}

      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error === undefined ? undefined : true,
        required,
      })}

      {hint === undefined ? null : (
        <span id={hintId} className="text-(length:--font-size-caption) text-(--color-text-muted)">
          {hint}
        </span>
      )}

      {/*
        `role="alert"` so a refusal reaches somebody who is not looking at this field — which is
        the normal case, because a server-side error arrives after the submit moved their attention
        to the button.
      */}
      {error === undefined ? null : (
        <span
          id={errorId}
          role="alert"
          className="text-(length:--font-size-caption) text-(--color-danger)"
        >
          {error}
        </span>
      )}
    </div>
  );
}
